import { Sparkles, ArrowRight } from 'lucide-react';
import { useCheckout } from '@/components/CheckoutContext';

// ─── Real DesignCarat product renders ─────────────────────────────────────
// All 14 images are square, photographed on pure black with reflective gold
// settings — they blend seamlessly into the dark marquee section.
// Files live in /app/frontend/public/product-images/jewel-01.png … jewel-14.png
const PRODUCT_IMAGES = Array.from(
  { length: 14 },
  (_, i) => `/product-images/jewel-${String(i + 1).padStart(2, '0')}.png`
);

// Split 7 / 7 across two rows. Each row is duplicated for a seamless loop.
const ROW_TOP = PRODUCT_IMAGES.slice(0, 7).concat(PRODUCT_IMAGES.slice(0, 7));
const ROW_BOTTOM = PRODUCT_IMAGES.slice(7, 14).concat(PRODUCT_IMAGES.slice(7, 14));
// ───────────────────────────────────────────────────────────────────────────

function MarqueeCard({ src, idx }) {
  return (
    <div
      className="marquee-card relative shrink-0 w-44 h-44 sm:w-52 sm:h-52 md:w-56 md:h-56 rounded-2xl overflow-hidden group"
      style={{
        background: '#000',
        border: '1px solid rgba(253,195,161,0.22)',
        boxShadow:
          '0 22px 50px -20px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
    >
      <img
        src={src}
        alt={`DesignCarat masterpiece ${idx + 1}`}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        draggable={false}
      />
      {/* Permanent gold inner ring (subtle) */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(253,195,161,0.18)',
        }}
      />
      {/* Brighter gold glow on hover */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
        style={{
          boxShadow:
            'inset 0 0 0 1px rgba(253,195,161,0.7), inset 0 0 36px rgba(253,195,161,0.18)',
        }}
      />
      {/* Top sheen to enhance the diamond highlights */}
      <div
        className="absolute inset-x-0 top-0 h-1/3 pointer-events-none opacity-60"
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,231,194,0.06), transparent)',
        }}
      />
    </div>
  );
}

export default function InfiniteRenderMarquee() {
  const { open } = useCheckout();
  return (
    <section
      data-testid="render-marquee-section"
      className="relative overflow-hidden py-16 sm:py-20"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 0%, #1a120a 0%, #0a0606 55%, #050303 100%)',
      }}
    >
      {/* Decorative gold glows */}
      <div
        className="pointer-events-none absolute -top-32 left-1/4 w-96 h-96 rounded-full opacity-30"
        style={{
          background:
            'radial-gradient(circle, rgba(253,195,161,0.55), transparent 65%)',
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-32 right-1/4 w-96 h-96 rounded-full opacity-20"
        style={{
          background:
            'radial-gradient(circle, rgba(255,200,120,0.5), transparent 65%)',
        }}
      />
      {/* Subtle vignette/border to separate from light page */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(253,195,161,0.4), transparent)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(253,195,161,0.4), transparent)',
        }}
      />

      {/* ───────── Heading ───────── */}
      <div className="relative max-w-5xl mx-auto px-6 text-center mb-10 sm:mb-14">
        <span
          data-testid="render-marquee-eyebrow"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{
            background: 'rgba(253,195,161,0.12)',
            border: '1px solid rgba(253,195,161,0.28)',
            color: '#FDC3A1',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <Sparkles className="w-3 h-3" />
          The Vault, In Motion
        </span>
        <h2
          data-testid="render-marquee-heading"
          className="mt-5 font-heading font-black text-3xl sm:text-4xl md:text-5xl leading-[1.1]"
          style={{
            backgroundImage:
              'linear-gradient(120deg, #FFE7C2 0%, #FDC3A1 35%, #FFD27A 60%, #FFEFD0 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            textShadow: '0 0 60px rgba(253,195,161,0.18)',
          }}
        >
          Over 4,000+ Masterpieces Inside <span className="inline-block">💎</span>
        </h2>
        <p
          className="mt-4 font-body text-sm sm:text-base max-w-xl mx-auto"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          A peek at the production-ready CAD renders waiting in the bundle.
          Pause to take a closer look.
        </p>
      </div>

      {/* ───────── Marquee group (hover anywhere to pause both rows) ───────── */}
      <div className="relative marquee-group">
        {/* Edge fade masks */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 z-10"
          style={{
            background:
              'linear-gradient(90deg, rgba(5,3,3,1) 0%, rgba(5,3,3,0) 100%)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 z-10"
          style={{
            background:
              'linear-gradient(270deg, rgba(5,3,3,1) 0%, rgba(5,3,3,0) 100%)',
          }}
        />

        {/* Top row → scrolls LEFT */}
        <div className="overflow-hidden">
          <div
            data-testid="marquee-row-top"
            className="marquee-track marquee-left flex gap-4 sm:gap-5 w-max"
          >
            {ROW_TOP.map((src, i) => (
              <MarqueeCard key={`top-${i}`} src={src} idx={i} />
            ))}
          </div>
        </div>

        {/* Bottom row → scrolls RIGHT */}
        <div className="overflow-hidden mt-4 sm:mt-5">
          <div
            data-testid="marquee-row-bottom"
            className="marquee-track marquee-right flex gap-4 sm:gap-5 w-max"
          >
            {ROW_BOTTOM.map((src, i) => (
              <MarqueeCard key={`bot-${i}`} src={src} idx={i + 6} />
            ))}
          </div>
        </div>
      </div>

      {/* ───────── Section CTA ───────── */}
      <div className="relative mt-12 sm:mt-14 flex flex-col items-center px-6 text-center">
        <p
          className="font-body text-xs sm:text-sm mb-4 max-w-md"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Unlock the full 900&nbsp;GB vault &middot; lifetime access &middot; instant download
        </p>
        <button
          data-testid="marquee-cta-button"
          onClick={() => open('marquee_cta')}
          className="btn-accent cta-pulse inline-flex items-center justify-center gap-3 text-sm md:text-base px-8 py-4 group"
          style={{
            boxShadow:
              '0 16px 48px -10px rgba(253,195,161,0.6), 0 4px 14px rgba(0,0,0,0.25)',
          }}
        >
          🚀 Grab Bundle Now &bull; ₹999
          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <span
          className="mt-3 font-body text-[11px] tracking-wide"
          style={{ color: 'rgba(253,195,161,0.7)' }}
        >
          Secure UPI Checkout &middot; 60-second access
        </span>
      </div>

      {/* Scoped marquee animation */}
      <style>{`
        @keyframes marqueeLeft  { from { transform: translateX(0); }    to { transform: translateX(-50%); } }
        @keyframes marqueeRight { from { transform: translateX(-50%); } to { transform: translateX(0); }    }

        .marquee-track  { will-change: transform; }
        .marquee-left   { animation: marqueeLeft  48s linear infinite; }
        .marquee-right  { animation: marqueeRight 48s linear infinite; }

        /* Pause both rows when hovering anywhere in the group */
        .marquee-group:hover .marquee-track { animation-play-state: paused; }

        /* Touch devices: allow tap-to-pause via :active */
        .marquee-group:active .marquee-track { animation-play-state: paused; }

        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
}
